import { useEffect, useRef, useState } from "react";
import { Bot, X, Send, Loader2, Check, Sparkles, RotateCcw, Mail, Users2, CheckCircle2, GitBranch, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const SID_KEY = "dealsops_operator_sid";
const newSid = () => (crypto?.randomUUID ? crypto.randomUUID() : `op-${Date.now()}-${Math.random().toString(16).slice(2)}`);
const getSid = () => { let s = localStorage.getItem(SID_KEY); if (!s) { s = newSid(); localStorage.setItem(SID_KEY, s); } return s; };

function OfferCard({ card }) {
  const o = card.offer || {};
  return (
    <div className="mt-2 rounded-lg border border-border/60 bg-background/60 p-3 text-xs space-y-2" data-testid="operator-card-brief">
      <div className="font-mono uppercase tracking-wider text-[10px] text-amber-500">Offer preview · {card.tone} · {card.motion}</div>
      <div><span className="text-muted-foreground">Product:</span> <span className="font-medium">{o.product}</span></div>
      {o.desired_outcome && <div><span className="text-muted-foreground">Outcome:</span> {o.desired_outcome}</div>}
      <div className="font-mono uppercase tracking-wider text-[10px] text-muted-foreground pt-1">3-step sequence · {card.campaign_name}</div>
      {(card.steps || []).map((s, i) => (
        <div key={i} className="rounded-md bg-accent/30 p-2">
          <div className="flex items-center gap-1.5 text-[11px]"><span className="text-amber-500 font-mono">D{s.day}</span> <span className="font-medium">{s.purpose}</span></div>
          {s.subject && <div className="text-[11px] mt-0.5 font-medium">{s.subject}</div>}
          <div className="text-[11px] text-muted-foreground whitespace-pre-line line-clamp-3 mt-0.5">{s.body}</div>
        </div>
      ))}
    </div>
  );
}

function LeadsCard({ card }) {
  const leads = card.leads || [];
  return (
    <div className="mt-2 rounded-lg border border-border/60 bg-background/60 p-3 text-xs" data-testid="operator-card-leads">
      <div className="font-mono uppercase tracking-wider text-[10px] text-amber-500 mb-2 flex items-center gap-1.5"><Users2 className="h-3 w-3" /> {leads.length} leads</div>
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {leads.map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-2">
            <div className="min-w-0"><div className="font-medium truncate">{l.name} <span className="text-muted-foreground font-normal">· {l.title}</span></div><div className="text-muted-foreground truncate">{l.company} · {l.sector}</div></div>
            <span className="shrink-0 text-amber-500 font-mono">{l.fit_score}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ card, icon: Icon, label, testid }) {
  const entries = Object.entries(card).filter(([k]) => !["type"].includes(k) && typeof card[k] !== "object");
  return (
    <div className="mt-2 rounded-lg border border-border/60 bg-background/60 p-3 text-xs" data-testid={testid}>
      <div className="font-mono uppercase tracking-wider text-[10px] text-amber-500 mb-1.5 flex items-center gap-1.5">{Icon && <Icon className="h-3 w-3" />} {label}</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {entries.map(([k, v]) => <span key={k}><span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}:</span> <span className="font-medium">{String(v)}</span></span>)}
      </div>
    </div>
  );
}

function CardView({ card }) {
  if (!card) return null;
  switch (card.type) {
    case "brief": return <OfferCard card={card} />;
    case "leads": return <LeadsCard card={card} />;
    case "saved": return <StatCard card={{ name: card.name }} icon={Check} label={card.label} testid="operator-card-saved" />;
    case "enroll": return <StatCard card={card} icon={Send} label="Enrolled" testid="operator-card-enroll" />;
    case "approve": return <StatCard card={card} icon={CheckCircle2} label="First-touch approved" testid="operator-card-approve" />;
    case "today": return <StatCard card={card.counts || {}} icon={Inbox} label="Today" testid="operator-card-today" />;
    case "sent": return <StatCard card={card} icon={Mail} label="Sent due follow-ups" testid="operator-card-sent" />;
    case "classify": return <StatCard card={card} icon={Inbox} label="Reply classified" testid="operator-card-classify" />;
    case "deal": return <StatCard card={card} icon={GitBranch} label="Deal moved" testid="operator-card-deal" />;
    case "mailbox": return <StatCard card={{ gmail: card.gmail?.health || (card.gmail?.connected ? "ok" : "not connected"), outlook: card.outlook?.health || (card.outlook?.connected ? "ok" : "not connected"), active: card.active_sender || "none" }} icon={Mail} label="Mailbox status" testid="operator-card-mailbox" />;
    case "resume": return <StatCard card={card} icon={RotateCcw} label="Resumed paused sends" testid="operator-card-resume" />;
    default: return null;
  }
}

export function OperatorRail() {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(getSid);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("dealsops:open-operator", onOpen);
    return () => window.removeEventListener("dealsops:open-operator", onOpen);
  }, []);

  useEffect(() => {
    if (open && !loaded) {
      api.get(`/operator/session/${sid}`).then((r) => { setMessages(r.data.messages || []); setPending(r.data.pending || null); setLoaded(true); }).catch(() => setLoaded(true));
    }
  }, [open, loaded, sid]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { id: `local-${Date.now()}`, role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/operator/chat", { session_id: sid, message: text });
      setMessages(data.messages || []); setPending(data.pending || null);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Operator hit an error"); } finally { setBusy(false); }
  };

  const decide = async (decision) => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/operator/confirm", { session_id: sid, confirm_id: pending.id, decision });
      setMessages(data.messages || []); setPending(data.pending || null);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Could not complete that"); } finally { setBusy(false); }
  };

  const newChat = () => {
    const s = newSid(); localStorage.setItem(SID_KEY, s); setSid(s); setMessages([]); setPending(null); setLoaded(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="operator-toggle"
          className="fixed right-0 top-1/2 -translate-y-1/2 z-40 flex items-center gap-2 rounded-l-xl bg-amber-500 text-black pl-3 pr-3.5 py-3 shadow-lg hover:bg-amber-400 transition-colors">
          <Bot className="h-5 w-5" /><span className="text-sm font-semibold hidden sm:block">Operator</span>
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="operator-panel">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:w-[400px] h-full bg-card border-l border-border/70 flex flex-col shadow-2xl">
            <div className="h-14 shrink-0 flex items-center justify-between px-4 border-b border-border/60">
              <div className="flex items-center gap-2"><span className="h-7 w-7 rounded-md bg-amber-500/15 text-amber-500 flex items-center justify-center"><Bot className="h-4 w-4" /></span><div><div className="text-sm font-semibold leading-none">DealsOps Operator</div><div className="text-[10px] text-muted-foreground mt-0.5">Runs outbound with you</div></div></div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={newChat} title="New chat" data-testid="operator-new-chat"><RotateCcw className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setOpen(false)} data-testid="operator-close"><X className="h-4 w-4" /></Button>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m) => (
                <div key={m.id} data-testid={`operator-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div className={`max-w-[92%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-amber-500 text-black rounded-br-sm" : "bg-accent/50 rounded-bl-sm"}`}>
                    {m.text && <div className="whitespace-pre-line">{m.text}</div>}
                    {m.card && <CardView card={m.card} />}
                    {m.confirm && (
                      <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-2.5" data-testid="operator-confirm-card">
                        <div className="text-xs text-foreground">{m.confirm.summary}</div>
                        {pending && pending.id === m.confirm.id && (
                          <div className="flex items-center gap-2 mt-2">
                            <Button size="sm" className="h-7 rounded-md bg-amber-500 text-black hover:bg-amber-400" onClick={() => decide("confirm")} disabled={busy} data-testid="operator-confirm-btn"><Check className="h-3.5 w-3.5 mr-1" /> Confirm</Button>
                            <Button size="sm" variant="ghost" className="h-7" onClick={() => decide("cancel")} disabled={busy} data-testid="operator-cancel-btn">Cancel</Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="bg-accent/50 rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking…</div></div>}
            </div>
            <div className="shrink-0 border-t border-border/60 p-3">
              <div className="flex items-end gap-2">
                <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} placeholder="Message the Operator…" data-testid="operator-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  className="flex-1 resize-none rounded-lg border border-border/70 bg-background px-3 py-2 text-sm focus:outline-none focus:border-amber-500 max-h-28" />
                <Button size="icon" className="h-9 w-9 rounded-lg bg-amber-500 text-black hover:bg-amber-400 shrink-0" onClick={send} disabled={busy || !input.trim()} data-testid="operator-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-1"><Sparkles className="h-3 w-3" /> Confirms before it approves or enrolls above 5. Never turns live sending on.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
